/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';
import { useElementSize } from '@mantine/hooks';
import {
	TApiResponseApiTokenPopulated,
	TBodyDeleteMultiple,
	TParsedApiToken,
	TResponseDeleteMultiple,
} from '../types';
import {
	TApiError,
	TAPIToken,
} from '@global-types/api/api';
import {
	TSelectOption,
	TSortStatus,
} from '@global-types/app/app';
import {
	UseMutateFunction,
	UseMutationResult,
} from '@tanstack/react-query';
import { ACLS } from '@config/constants/acls';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useApiTokens = () => {
	/**************************** Refs ****************************/
	const containerRef = useRef<HTMLDivElement | null>(
		null
	);
	const { checkAcl, checkAccess } = useAuthorization();
	/**************************** States ****************************/
	const [openDrawer, setOpenDrawer] = useState(false);
	const [
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		apiTokensPage,
		apiTokensPageSize,
		setApiTokensPage,
		apiTokensSelectedRecords,
		setApiTokensSelectedRecords,
		apiTokensOpenConfirmationModalDelete,
		setApiTokensOpenConfirmationModalDelete,
		apiTokensQueryParams,
		setApiTokensQueryParams,
		apiTokensSortStatus,
		setApiTokensSortStatus,
		setApiTokensSortParams,
		apiTokensSortParams,
		setApiTokensPageSize,
		setApiTokensSelectedRow,
		apiTokensSelectedRow,
		apiTokensLastAccessTokenCreated,
		setApiTokensLastAccessTokenCreated,
		apiTokensDatatableOptions,
		setApiTokensDatatableOptions,
	} = useStore();

	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setOpenDrawerAddApiToken(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setApiTokensPageSize(10);
		setApiTokensQueryParams({});
		setApiTokensLastAccessTokenCreated(null);
		setApiTokensPage(1);
		setApiTokensSortParams('-createdAt');
		setApiTokensSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/
	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: TSortStatus) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'apiToken':
				sortParams = 'apiToken';
				break;
			case 'producer':
				sortParams = 'entity';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setApiTokensSortParams(sortParams);
		} else {
			setApiTokensSortParams('-' + sortParams);
		}
	};

	const handleOrganizationFilter = (
		organizations: TSelectOption[]
	) => {
		setApiTokensSelectedRecords([]);
		setApiTokensPage(1);
		const queryParamsProjects = {
			organization: organizations.map(
				(_organization: TSelectOption) =>
					_organization.value
			),
			site: [],
		};

		setApiTokensQueryParams({
			...apiTokensQueryParams,
			...queryParamsProjects,
		});
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (
		remove: UseMutationResult<
			TApiResponseApiTokenPopulated,
			TApiError,
			string,
			unknown
		>
	) => {
		setApiTokensOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			apiTokensOpenConfirmationModalDelete.params.id
		);
		setOpenDrawer(false);
		setApiTokensSelectedRecords([]);
		setApiTokensLastAccessTokenCreated(null);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setApiTokensOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		setApiTokensOpenConfirmationModalDelete({
			open: true,
			params: apiTokensSelectedRow,
		});
	};

	/*************************************** Delete multiple Items  ***************************************/

	const handleConfirmDeleteMultipleItem =
		(removeMultiple: {
			mutate: UseMutateFunction<
				TResponseDeleteMultiple,
				TApiError,
				TBodyDeleteMultiple,
				unknown
			>;
			isLoading: boolean;
			isIdle: boolean;
		}) => {
			const idsList = [...apiTokensSelectedRecords].map(
				(_apiToken) => _apiToken.id
			);
			removeMultiple.mutate({
				ids: idsList,
			});

			setApiTokensSelectedRecords([]);
			setOpenSelectedItemsToDeleteModal(false);
		};

	/*************************************** Datatables  ***************************************/
	const handleViewMore = (row: TParsedApiToken) => {
		setApiTokensSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: {
		record: TAPIToken;
	}) => {
		if (
			checkAcl(ACLS.API_TOKENS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setApiTokensSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSelectedRecordsChange = (
		selectedRecords: TAPIToken
	) => {
		setApiTokensSelectedRecords(selectedRecords);
	};

	const handleSortStatusChange = (data: TSortStatus) => {
		handleSortParams(data);
		setApiTokensSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlepaginationchangesize = function (
		_size: string | number
	) {
		setApiTokensPageSize(_size);
		setApiTokensPage(1);
	};

	const handlechangepagination = (data: number) => {
		setApiTokensPage(data);
	};

	const handleResetSelectedItems = () => {
		setApiTokensSelectedRecords([]);
	};
	/********************************************************************************************/

	return {
		handleResetSelectedItems,
		handlepaginationchangesize,
		handlechangepagination,
		handleSortStatusChange,
		handleSelectedRecordsChange,
		handleRowClicked,
		handleViewMore,
		handleConfirmDeleteMultipleItem,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		apiTokensPage,
		apiTokensPageSize,
		apiTokensSelectedRecords,
		setApiTokensSelectedRecords,
		apiTokensOpenConfirmationModalDelete,
		setApiTokensOpenConfirmationModalDelete,
		apiTokensQueryParams,
		apiTokensSortStatus,
		apiTokensSortParams,
		apiTokensSelectedRow,
		apiTokensLastAccessTokenCreated,
		setApiTokensLastAccessTokenCreated,
		apiTokensDatatableOptions,
		setApiTokensDatatableOptions,
	};
};
